import { IonIcon, IonModal } from "@ionic/react";
import {
  sendSharp,
  closeOutline,
  chevronForwardOutline,
  chevronDownOutline,
  listOutline,
} from "ionicons/icons";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  EnhancedTitrationCardProps,
  TitrationData,
  TitrationSession,
} from "../../../contexts/types";
import { TitrationLoading } from "./ProgressIndicator";
import { useBLE } from "../../../hooks/useBle";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";

// Updated interface for props
interface UpdatedEnhancedTitrationCardProps
  extends Omit<EnhancedTitrationCardProps, "onSync"> {
  onSync: (therapy: TitrationSession) => void;
}

// Stable TherapySelector that only re-renders when explicitly needed
const TherapySelector = React.memo(
  ({
    isOpen,
    onClose,
    onSelect,
    titrationTherapiesList,
    modalKey, // Add key to force re-creation when needed
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (therapy: TitrationSession) => void;
    titrationTherapiesList: TitrationSession[];
    modalKey: string;
  }) => {
    // Refs to persist scroll position
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef(0);

    // console.log(
    //   `TherapySelector RENDER - Key: ${modalKey}, Open: ${isOpen}, List Length: ${titrationTherapiesList?.length}`
    // );

    // Background scroll lock
    useEffect(() => {
      if (isOpen) {
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = "hidden";

        return () => {
          document.body.style.overflow = originalStyle;
        };
      }
    }, [isOpen]);

    // Restore scroll position after each render
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (container && isOpen && scrollPositionRef.current > 0) {
        requestAnimationFrame(() => {
          container.scrollTop = scrollPositionRef.current;
          console.log(`Restored scroll to: ${scrollPositionRef.current}`);
        });
      }
    });

    // Handle scroll with persistence
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const newScrollTop = e.currentTarget.scrollTop;
      scrollPositionRef.current = newScrollTop;
      console.log(`Scroll position saved: ${newScrollTop}`);
    }, []);

    const handleModalClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleBackdropClick = useCallback(() => {
      onClose();
    }, [onClose]);

    if (!isOpen || !titrationTherapiesList) return null;

    return (
      <div
        className="fixed inset-0 z-[60]"
        onClick={handleBackdropClick}
        style={{
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          width: "100%",
          padding: "0 0 50px 0",
          touchAction: "none",
        }}
      >
        <div
          onClick={handleModalClick}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px 20px",
            gap: "32px",
            width: "362px",
            maxWidth: "calc(100vw - 32px)",
            height: "385px",
            background: "#222222",
            borderRadius: "30px",
            position: "relative",
            touchAction: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "0px",
              gap: "30px",
              width: "100%",
              maxWidth: "309px",
              height: "28px",
              position: "relative",
            }}
          >
            <h2
              style={{
                width: "100%",
                height: "28px",
                fontFamily: "Figtree, sans-serif",
                fontStyle: "normal",
                fontWeight: "700",
                fontSize: "24px",
                lineHeight: "28px",
                textAlign: "center",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Select Therapy
            </h2>
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                width: "30px",
                height: "30px",
                right: "0px",
                top: "-12.66px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IonIcon
                icon={closeOutline}
                style={{
                  color: "#B0B4B7",
                  width: "24px",
                  height: "24px",
                }}
              />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0px",
              gap: "6px",
              width: "100%",
              maxWidth: "322px",
              height: "280px",
            }}
          >
            <h3
              style={{
                width: "100%",
                height: "14px",
                fontFamily: "Figtree, sans-serif",
                fontStyle: "normal",
                fontWeight: "500",
                fontSize: "12px",
                lineHeight: "14px",
                textTransform: "uppercase",
                color: "#FFFFFF",
                margin: 0,
                flexShrink: 0,
              }}
            >
              {titrationTherapiesList.length > 0
                ? `LAST ${titrationTherapiesList.length} THERAPIES`
                : "NO Manual Therapies Found on Device"}
            </h3>

            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "0px",
                width: "100%",
                height: "260px",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: "#666 #222",
                scrollBehavior: "auto",
                WebkitOverflowScrolling: "touch",
                paddingRight: "8px",
                marginRight: "-8px",
                willChange: "scroll-position",
                transform: "translateZ(0)",
              }}
              onWheel={(e) => {
                e.stopPropagation();
                const element = e.currentTarget;
                const { scrollTop, scrollHeight, clientHeight } = element;

                if (
                  (scrollTop === 0 && e.deltaY < 0) ||
                  (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0)
                ) {
                  e.preventDefault();
                }
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              {titrationTherapiesList.map((therapy, index) => (
                <button
                  key={`therapy-${therapy.refId}-${therapy.date}`}
                  onClick={() => {
                    console.log(`Selected therapy: ${therapy.refId}`);
                    onSelect(therapy);
                    onClose();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0px",
                    gap: "12px",
                    width: "100%",
                    minHeight: "52px",
                    background: "#222222",
                    borderTop: index === 0 ? "none" : "1px solid #363636",
                    borderRadius:
                      index === 0
                        ? "12px 12px 0px 0px"
                        : index === titrationTherapiesList.length - 1
                        ? "0px 0px 12px 12px"
                        : "0px",
                    border: "none",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "0px",
                      gap: "8px",
                      height: "20px",
                      margin: "0 auto",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "Figtree, sans-serif",
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "0.374px",
                        color: "#FFFFFF",
                      }}
                    >
                      {therapy.date}
                    </span>
                    <span
                      style={{
                        fontFamily: "Figtree, sans-serif",
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: "14px",
                        lineHeight: "17px",
                        letterSpacing: "0.374px",
                        color: "#B0B4B7",
                      }}
                    >
                      ({therapy.time})
                    </span>
                    {therapy.locallyAvailable && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#22C55E",
                          marginLeft: "4px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>

                  <IonIcon
                    icon={chevronForwardOutline}
                    style={{
                      width: "20px",
                      height: "20px",
                      color: "#B0B4B7",
                      flexShrink: 0,
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the modal key changes or open state changes
    const shouldSkipRender =
      prevProps.modalKey === nextProps.modalKey &&
      prevProps.isOpen === nextProps.isOpen;

    // console.log("TherapySelector memo check:", {
    //   prevKey: prevProps.modalKey,
    //   nextKey: nextProps.modalKey,
    //   prevOpen: prevProps.isOpen,
    //   nextOpen: nextProps.isOpen,
    //   shouldSkipRender,
    // });

    return shouldSkipRender;
  }
);

TherapySelector.displayName = "TherapySelector";

// Enhanced Titration Summary Card Component
export const EnhancedTitrationCard: React.FC<
  UpdatedEnhancedTitrationCardProps
> = ({ data, isLoading, onSync }) => {
  // console.log(
  //   "EnhancedTitrationCard RENDER at",
  //   new Date().toLocaleTimeString()
  // );

  const {
    isConnected,
    storedTitrationDeviceId,
    titrationTherapiesList,
    openModal,
    device,
  } = useBLE();

  const [isTherapySelectorOpen, setIsTherapySelectorOpen] = useState(false);
  const [selectedTherapy, setSelectedTherapy] =
    useState<TitrationSession | null>(null);

  // Modal data management - freeze props when modal opens
  const [modalData, setModalData] = useState({
    therapyList: [] as TitrationSession[],
    isOpen: false,
    modalKey: "",
    timestamp: 0,
  });

  // Update the snapshot only when modal state changes
  useEffect(() => {
    if (isTherapySelectorOpen && !modalData.isOpen) {
      // Modal just opened - take snapshot
      const timestamp = Date.now();
      // console.log(
      //   "Modal opening - taking snapshot of therapy list",
      //   titrationTherapiesList?.length
      // );
      setModalData({
        therapyList: titrationTherapiesList || [],
        isOpen: true,
        modalKey: `modal-${timestamp}`,
        timestamp,
      });
    } else if (!isTherapySelectorOpen && modalData.isOpen) {
      // Modal just closed - reset
      // console.log("Modal closing - clearing snapshot");
      setModalData({
        therapyList: [],
        isOpen: false,
        modalKey: "",
        timestamp: 0,
      });
    }
  }, [isTherapySelectorOpen, modalData.isOpen, titrationTherapiesList]);

  // Create stable callback references using useRef
  const stableCallbacks = useRef({
    onClose: () => setIsTherapySelectorOpen(false),
    onSelect: (therapy: TitrationSession) => {
      setSelectedTherapy(therapy);
      onSync(therapy);
    },
  });

  // Update callbacks if onSync changes
  useEffect(() => {
    stableCallbacks.current.onSelect = (therapy: TitrationSession) => {
      setSelectedTherapy(therapy);
      onSync(therapy);
    };
  }, [onSync]);

  // **NEW: Landscape mode state**
  const [forcedLandscape, setForcedLandscape] = useState(false);
  const [showSummarySlideOver, setShowSummarySlideOver] = useState(false);
  const [preLandscapeState, setPreLandscapeState] = useState<{
    selectedTherapy: TitrationSession | null;
  } | null>(null);

  const isLandscape = forcedLandscape || window.innerWidth > window.innerHeight;

  // **NEW: Landscape orientation functions**
  const forceEnterLandscape = async () => {
    setPreLandscapeState({
      selectedTherapy,
    });
    setForcedLandscape(true);
    setShowSummarySlideOver(false);
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "landscape" });
      } catch (error) {
        console.warn("Failed to force landscape:", error);
      }
    }
  };

  const exitLandscape = async () => {
    setForcedLandscape(false);
    setShowSummarySlideOver(false);
    if (preLandscapeState) {
      setSelectedTherapy(preLandscapeState.selectedTherapy);
    }
    setPreLandscapeState(null);
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch (error) {
        console.warn("Failed to return to portrait:", error);
      }
    }
  };

  // Auto-select therapy with largest refId on mount - OPTIMIZED
  useEffect(() => {
    // console.log(
    //   "Therapy list effect triggered",
    //   titrationTherapiesList?.length
    // );
    if (
      titrationTherapiesList &&
      titrationTherapiesList.length > 0 &&
      !selectedTherapy
    ) {
      const therapyWithLargestRefId = titrationTherapiesList.reduce(
        (prev, current) =>
          parseInt(current.refId) > parseInt(prev.refId) ? current : prev
      );
      setSelectedTherapy(therapyWithLargestRefId);
      console.log("Auto-selected therapy:", therapyWithLargestRefId.refId);
    }
  }, [titrationTherapiesList, selectedTherapy]);

  const generateScaleLabels = (isLandscapeView = false) => {
    const labels = [];
    const interval = isLandscapeView ? 5 : 10;
    for (let i = 70; i >= -70; i -= interval) {
      labels.push(i);
    }
    return labels;
  };

  const PreferredIcon = () => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="flex-shrink-0"
    >
      <path
        d="M5 0C2.24 0 0 2.24 0 5C0 7.76 2.24 10 5 10C7.76 10 10 7.76 10 5C10 2.24 7.76 0 5 0ZM5 9C2.795 9 1 7.205 1 5C1 2.795 2.795 1 5 1C7.205 1 9 2.795 9 5C9 7.205 7.205 9 5 9ZM7.295 2.79L4 6.085L2.705 4.795L2 5.5L4 7.5L8 3.5L7.295 2.79Z"
        fill="#B0B4B7"
      />
    </svg>
  );

  const DislikedIcon = () => (
    <svg
      width="10"
      height="11"
      viewBox="0 0 10 11"
      fill="none"
      className="flex-shrink-0"
    >
      <path
        d="M5 0.375C2.2445 0.375 0 2.6195 0 5.375C0 8.1305 2.2445 10.375 5 10.375C7.7555 10.375 10 8.1305 10 5.375C10 2.6195 7.7555 0.375 5 0.375ZM5 1.375C7.21506 1.375 9 3.15994 9 5.375C9 7.59006 7.21506 9.375 5 9.375C2.78494 9.375 1 7.59006 1 5.375C1 3.15994 2.78494 1.375 5 1.375ZM3.35352 3.02148L2.64648 3.72852L4.29297 5.375L2.64648 7.02148L3.35352 7.72852L5 6.08203L6.64648 7.72852L7.35352 7.02148L5.70703 5.375L7.35352 3.72852L6.64648 3.02148L5 4.66797L3.35352 3.02148Z"
        fill="#FF001A"
      />
    </svg>
  );

  const ExpandIcon = () => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
      <rect width="30" height="30" rx="15" fill="#262626" />
      <path
        d="M20.8346 9.1665L15.8346 9.1665L15.8346 10.8332L19.168 10.8332L19.168 14.1665L20.8346 14.1665L20.8346 9.1665ZM14.168 19.1665L10.8346 19.1665L10.8346 15.8332L9.16797 15.8332L9.16797 20.8332L14.168 20.8332L14.168 19.1665Z"
        fill="#B0B4B7"
      />
    </svg>
  );

  const handleFetchFromDevice = () => {
    if ((!selectedTherapy || titrationTherapiesList.length === 0) && device) {
      openModal("TherapyNotFound", device);
    }
    if (selectedTherapy) {
      onSync(selectedTherapy);
    }
  };

  const renderPressureChart = (isLandscapeView = false) => {
    if (!data) return null;

    const groupedCycles = {
      preferred: data.cycles.filter(
        (c) => c.feedback === "preferred"
      ),
      none: data.cycles.filter(
        (c) => c.feedback === "none"
      ),
      disliked: data.cycles.filter(
        (c) => c.feedback === "disliked"
      ),
    };

    const sortedCycles = [
      ...groupedCycles.preferred,
      ...groupedCycles.none,
      ...groupedCycles.disliked,
    ];

    const scaleLabels = generateScaleLabels(isLandscapeView);
    const barHeight = isLandscapeView ? "16px" : "8px";
    const barSpacing = isLandscapeView ? "py-1.5" : "py-0.5";
    const scaleTextSize = isLandscapeView ? "text-sm" : "text-[10px]";

    return (
      <div className="mb-6">
        {!isLandscapeView && (
          <div className="mb-3">
            <span className="text-white text-sm">
              Therapy date: {
                (() => {
                  // Find the therapy in titrationTherapiesList matching selectedTherapy.refId
                  const therapy = titrationTherapiesList?.find(
                    (t) => t.refId === selectedTherapy?.refId
                  );
                  return therapy
                    ? `${therapy.date} ${therapy.time}`
                    : data.therapyDate;
                })()
              }
            </span>
          </div>
        )}

        {!isLandscapeView && (
          <div className="flex items-center gap-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-cyan-400 rounded"></div>
              <span className="text-gray-400 text-sm">Insp.</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-gray-400 text-sm">Pause</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-orange-400 rounded"></div>
              <span className="text-gray-400 text-sm">Exp.</span>
            </div>
          </div>
        )}

        <div
          className="relative"
          style={{
            height: isLandscapeView ? "calc(100vh - 180px)" : "auto",
          }}
        >
          <div
            className={`${
              isLandscapeView
                ? "overflow-y-auto"
                : "max-h-36 overflow-y-auto justify-center align-middle"
            } scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800`}
            style={{
              height: isLandscapeView ? "calc(100vh - 240px)" : "auto",
              paddingBottom: isLandscapeView ? "40px" : "0",
            }}
          >
            {sortedCycles.map((cycle) => {
              const totalRange = 160;
              const zeroPosition = 50;

              const expirationStart =
                cycle.expiration < 0
                  ? zeroPosition -
                    (Math.abs(cycle.expiration) / 80) * zeroPosition
                  : zeroPosition;
              const expirationWidth =
                cycle.expiration < 0
                  ? (Math.abs(cycle.expiration) / 80) * zeroPosition
                  : 0;

              const getPauseAndInspWidthAndStart = () => {
                if (cycle.inspiration > 0 && cycle.inspiration > cycle.pause) {
                  const pauseStart = zeroPosition;
                  const pauseWidth =
                    cycle.pause > 0 ? (cycle.pause / 80) * zeroPosition : 0;
                  const inspirationStart = pauseStart + pauseWidth;
                  const inspirationWidth =
                    cycle.inspiration > 0
                      ? ((cycle.inspiration - cycle.pause) / 80) * zeroPosition
                      : 0;
                  return {
                    pauseStart,
                    pauseWidth,
                    inspirationStart,
                    inspirationWidth,
                  };
                } else if (
                  cycle.inspiration > 0 &&
                  cycle.inspiration < cycle.pause
                ) {
                  const inspirationStart = zeroPosition;
                  const inspirationWidth =
                    cycle.inspiration > 0
                      ? (cycle.inspiration / 80) * zeroPosition
                      : 0;
                  const pauseStart = inspirationStart + inspirationWidth;
                  const pauseWidth =
                    cycle.pause > 0
                      ? ((cycle.pause - cycle.inspiration) / 80) * zeroPosition
                      : 0;
                  return {
                    pauseStart,
                    pauseWidth,
                    inspirationStart,
                    inspirationWidth,
                  };
                } else {
                  const pauseStart = zeroPosition;
                  const pauseWidth =
                    cycle.pause > 0 ? (cycle.pause / 80) * zeroPosition : 0;
                  const inspirationStart = pauseStart + pauseWidth;
                  const inspirationWidth =
                    cycle.inspiration > 0
                      ? ((cycle.inspiration + 1) / 80) * zeroPosition
                      : 0;
                  return {
                    pauseStart,
                    pauseWidth,
                    inspirationStart,
                    inspirationWidth,
                  };
                }
              };

              const {
                pauseStart,
                pauseWidth,
                inspirationStart,
                inspirationWidth,
              } = getPauseAndInspWidthAndStart();

              const isDisliked = cycle.feedback === "disliked";
              const expirationColor = isDisliked ? "#6B7280" : "bg-orange-400";
              const pauseColor = isDisliked ? "#9CA3AF" : "bg-green-500";
              const inspirationColor = isDisliked ? "#D1D5DB" : "bg-cyan-400";

              return (
                <div
                  key={cycle.id}
                  className={barSpacing}
                  style={{
                    backgroundColor: isDisliked
                      ? "rgba(255, 255, 255, 0.1)"
                      : "transparent",
                  }}
                >
                  <div className="flex items-center">
                    <div
                      className="relative rounded-sm overflow-hidden"
                      style={{
                        width: "calc(100% - 60px)",
                        height: barHeight,
                      }}
                    >
                      {cycle.expiration < 0 && (
                        <div
                          className="absolute top-0 h-full bg-orange-400"
                          style={{
                            left: `${expirationStart}%`,
                            width: `${expirationWidth}%`,
                            backgroundColor: isDisliked
                              ? expirationColor
                              : undefined,
                          }}
                        />
                      )}

                      {cycle.pause > 0 && (
                        <div
                          className="absolute top-0 h-full bg-green-500"
                          style={{
                            left: `${pauseStart}%`,
                            width: `${pauseWidth}%`,
                            backgroundColor: isDisliked
                              ? pauseColor
                              : undefined,
                          }}
                        />
                      )}

                      {cycle.inspiration > 0 && (
                        <div
                          className="absolute top-0 h-full bg-cyan-400"
                          style={{
                            left: `${inspirationStart}%`,
                            width: `${inspirationWidth}%`,
                            backgroundColor: isDisliked
                              ? inspirationColor
                              : undefined,
                          }}
                        />
                      )}
                    </div>

                    <div
                      className={`${
                        isLandscapeView ? "w-8" : "w-6"
                      } flex justify-center items-center flex-shrink-0 ml-3`}
                    >
                      {cycle.feedback === "preferred" && <PreferredIcon />}
                      {cycle.feedback === "disliked" && <DislikedIcon />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isLandscapeView ? (
            <div className="flex justify-end">
              <div
                className="absolute bottom-0 left-0"
                style={{
                  width: "calc(100% - 60px)",
                  zIndex: 10,
                  backgroundColor: "#000000",
                  paddingTop: "4px",
                }}
              >
                <div className="relative h-6">
                  <div className="flex justify-between w-full">
                    {scaleLabels.map((label, index) => {
                      const position = ((label + 80) / 160) * 100;
                      return (
                        <span
                          key={index}
                          className={`absolute text-gray-400 ${scaleTextSize} text-center transform -translate-x-1/2 whitespace-nowrap font-medium`}
                          style={{
                            left: `${position}%`,
                            minWidth: "20px",
                          }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div
                className="absolute bottom-0 "
                style={{
                  zIndex: 10,
                  backgroundColor: "#000000",
                  paddingTop: "4px",
                }}
              >
                <div className="relative h-6">
                  <div className="flex justify-between w-full"></div>
                  <span
                    className={`fixed text-gray-400 ${scaleTextSize} text-center whitespace-nowrap font-medium`}
                    style={{
                      right: "24px",
                      transform: "none",
                      paddingRight: "0",
                      zIndex: 100,
                      background: "#000",
                      borderRadius: "8px",
                    }}
                  >
                    cmH₂O
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex ">
              <div
                className="relative mt-6"
                style={{ width: "calc(100% - 60px)" }}
              >
                <div className="relative h-6 overflow-x-hidden">
                  <div className="flex justify-between min-w-full">
                    {scaleLabels.map((label, index) => {
                      const position = ((label + 80) / 160) * 100;
                      return (
                        <span
                          key={index}
                          className={`absolute text-gray-400 ${scaleTextSize} text-center transform -translate-x-1/2 whitespace-nowrap font-medium`}
                          style={{
                            left: `${position}%`,
                            minWidth: "20px",
                          }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              <span
                className={`absolute text-gray-400 relative mt-6 ${scaleTextSize} text-center whitespace-nowrap font-medium`}
                style={{
                  right: 0,
                  left: "auto",
                  top: 0,
                  transform: "none",
                  paddingRight: "0",
                }}
              >
                cmH₂O
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPressureTable = (isLandscapeView = false) => {
    if (!data) return null;

    const headerTextSize = isLandscapeView ? "text-sm" : "text-xs";
    const cellTextSize = isLandscapeView ? "text-sm" : "text-xs";
    const cellPadding = isLandscapeView ? "py-4 px-6" : "py-3 px-4";
    const headerPadding = isLandscapeView ? "py-5 px-6" : "py-4 px-4";

    return (
      <div>
        {!isLandscapeView && (
          <>
            <div className="w-full h-px bg-gray-600 mb-4"></div>
            <h4 className="text-white font-medium mb-4">
              Summary of Preferred Cycles
            </h4>
          </>
        )}

        <div className="flex flex-col border border-gray-700 rounded-lg overflow-hidden">
          <div className="flex">
            <div
              className={`flex justify-center items-center ${headerPadding} border-r border-gray-700`}
              style={{ width: isLandscapeView ? "140px" : "110px" }}
            >
              <span
                className={`text-gray-400 ${headerTextSize} font-bold`}
              ></span>
            </div>
            <div
              className={`flex-1 flex justify-center items-center ${headerPadding} border-r border-gray-700`}
            >
              <span className={`text-white ${headerTextSize} font-bold`}>
                Pause
              </span>
            </div>
            <div
              className={`flex-1 flex justify-center items-center ${headerPadding} border-r border-gray-700`}
            >
              <span className={`text-white ${headerTextSize} font-bold`}>
                Insp.
              </span>
            </div>
            <div
              className={`flex-1 flex justify-center items-center ${headerPadding}`}
            >
              <span className={`text-white ${headerTextSize} font-bold`}>
                Exp.
              </span>
            </div>
          </div>

          {data.pressureSettings.map((setting, index) => (
            <div key={index} className="flex border-t border-gray-700">
              <div
                className={`flex justify-center items-center ${cellPadding} border-r border-gray-700`}
                style={{ width: isLandscapeView ? "140px" : "110px" }}
              >
                <span
                  className={`text-white ${cellTextSize} font-bold leading-tight text-center`}
                >
                  {(() => {
                    let label = setting.label;

                    // Remove space after "cm" (case-insensitive)
                    label = label.replace(/cm\s+/gi, "cm");

                    // Replace all "2" with subscript ₂
                    label = label.replace(/2/g, "₂");

                    // Only wrap "sec" part in brackets (case-insensitive)
                    label = label.replace(/(sec)/gi, "($1)");

                    return label;
                  })()}
                </span>
              </div>
              <div
                className={`flex-1 flex justify-center items-center ${cellPadding} border-r border-gray-700`}
              >
                <span className={`text-white ${cellTextSize}`}>
                  {setting.pause}
                </span>
              </div>
              <div
                className={`flex-1 flex justify-center items-center ${cellPadding} border-r border-gray-700`}
              >
                <span className={`text-white ${cellTextSize}`}>
                  {setting.insp}
                </span>
              </div>
              <div
                className={`flex-1 flex justify-center items-center ${cellPadding}`}
              >
                <span className={`text-white ${cellTextSize}`}>
                  {setting.exp}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <TitrationLoading />;
    }

    if (!data) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <button
            onClick={handleFetchFromDevice}
            className="bg-gradient-to-r from-cyan-500 to-sky-500 text-white px-8 py-3 rounded-full font-medium flex items-center gap-2"
            style={{
              background: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
              borderRadius: "30px",
              fontFamily:
                "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: "600",
              fontSize: "16px",
              padding: "12px 25px",
              height: "46px",
            }}
          >
            <IonIcon
              icon={sendSharp}
              style={{
                fontSize: "18px",
                color: "white",
              }}
            />
            Fetch from Device
          </button>
        </div>
      );
    }

    return (
      <div>
        {renderPressureChart()}
        {renderPressureTable()}
      </div>
    );
  };

  return (
    <>
      <div
        className="rounded-xl mb-4 overflow-hidden mx-auto"
        style={{ backgroundColor: "#1C1C1E", width: "100%" }}
      >
        {/* **UPDATED: Responsive Header Layout** */}
        <div
          className="px-4 py-3 border-b"
          style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
        >
          {/* Row 1: Title + Expand Icon */}
          <div className="flex justify-between items-center">
            <h3 className="text-white text-lg font-medium">
              TITRATION SUMMARY
            </h3>
            <div className="flex items-center gap-3">
              {/* Therapy selector - hidden on small screens */}
              {titrationTherapiesList && titrationTherapiesList.length > 0 && (
                <div className="hidden sm:block" style={{ minWidth: "50px" }}>
                  <button
                    onClick={() => setIsTherapySelectorOpen(true)}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "4px 8px 4px 12px",
                      gap: "2px",
                      maxWidth: "100%",
                      minWidth: "50px",
                      height: "32px",
                      background: "#262626",
                      borderRadius: "30px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        height: "20px",
                        fontFamily: "Figtree, sans-serif",
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: "12px",
                        lineHeight: "20px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "center",
                        color: "#B0B4B7",
                        justifyContent: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Therapy date:{" "}
                      {selectedTherapy
                        ? `${selectedTherapy.date} ${selectedTherapy.time}`
                        : "Select Therapy"}
                    </span>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        transform: "rotate(90deg)",
                        flex: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IonIcon
                        icon={chevronForwardOutline}
                        style={{
                          color: "#B0B4B7",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  </button>
                </div>
              )}

              {/* Expand icon - always visible */}
              {
                <button
                  onClick={forceEnterLandscape}
                  className="hover:opacity-80 transition-opacity"
                >
                  <ExpandIcon />
                </button>
              }
            </div>
          </div>

          {/* Row 2: Therapy selector - shown only on small screens */}
          {titrationTherapiesList && titrationTherapiesList.length > 0 && (
            <div className="flex justify-end mt-3 sm:hidden">
              <button
                onClick={() => setIsTherapySelectorOpen(true)}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "4px 8px 4px 12px",
                  gap: "2px",
                  maxWidth: "100%",
                  minWidth: "150px",
                  height: "32px",
                  background: "#262626",
                  borderRadius: "30px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    height: "20px",
                    fontFamily: "Figtree, sans-serif",
                    fontStyle: "normal",
                    fontWeight: "400",
                    fontSize: "12px",
                    lineHeight: "20px",
                    display: "flex",
                    alignItems: "center",
                    textAlign: "center",
                    color: "#B0B4B7",
                    justifyContent: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  Therapy date:{" "}
                  {selectedTherapy
                    ? `${selectedTherapy.date} ${selectedTherapy.time}`
                    : "Select Therapy"}
                </span>
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    transform: "rotate(90deg)",
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IonIcon
                    icon={chevronForwardOutline}
                    style={{
                      color: "#B0B4B7",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </button>
            </div>
          )}
        </div>

        {!isConnected && storedTitrationDeviceId && data && (
          <div
            className="px-4 py-2 border-b"
            style={{
              backgroundColor: "#2A2A2C",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <p
              className="text-sm font-normal"
              style={{ color: "rgba(255, 255, 255, 0.6)" }}
            >
              Showing data from device: {storedTitrationDeviceId}
            </p>
          </div>
        )}

        <div className="p-4">{renderContent()}</div>

        {/* Use frozen modal data with stable key */}
        <TherapySelector
          isOpen={isTherapySelectorOpen}
          onClose={stableCallbacks.current.onClose}
          onSelect={stableCallbacks.current.onSelect}
          titrationTherapiesList={modalData.therapyList}
          modalKey={modalData.modalKey}
        />
      </div>

      {isLandscape && forcedLandscape && (
      <IonModal
        isOpen={isLandscape && forcedLandscape}
        showBackdrop={false}
        className="landscape-modal"
        enterAnimation={undefined}
        leaveAnimation={undefined}
      >
        <div className="min-h-screen bg-black text-white">
          <div
            style={{
              padding: "16px 24px",
              background: "#000000",
              height: "100vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
                minHeight: "40px",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  onClick={exitLandscape}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "16px",
                    transition: "all 0.2s ease",
                    borderRadius: "6px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.8";
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#B0B4B7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <h3
                  className="text-white text-lg font-medium"
                  style={{
                    fontFamily:
                      "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
                    fontWeight: "600",
                    fontSize: "18px",
                    margin: 0,
                  }}
                >
                  TITRATION SUMMARY
                </h3>
              </div>

              <button
                onClick={() => setShowSummarySlideOver(!showSummarySlideOver)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: "#262626",
                  borderRadius: "20px",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#363636";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#262626";
                }}
              >
                <IonIcon
                  icon={listOutline}
                  style={{
                    width: "16px",
                    height: "16px",
                    color: "#B0B4B7",
                  }}
                />
                <span
                  style={{
                    fontFamily: "Figtree, sans-serif",
                    fontSize: "14px",
                    color: "#B0B4B7",
                    fontWeight: "500",
                  }}
                >
                  Summary
                </span>
              </button>
            </div>

            {data && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  flexShrink: 0,
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <span
                  style={{
                    fontFamily: "Figtree, sans-serif",
                    fontSize: "16px",
                    color: "#FFFFFF",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}
                >
                  Therapy date: {data.therapyDate}
                </span>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-cyan-400 rounded"></div>
                    <span className="text-gray-400 text-sm">Insp</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-gray-400 text-sm">Pause</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-400 rounded"></div>
                    <span className="text-gray-400 text-sm">Exp</span>
                  </div>
                </div>
              </div>
            )}

            <div
              style={{
                flex: 1,
                overflow: "hidden",
                minHeight: 0,
                paddingBottom: "20px",
              }}
            >
              {data ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {renderPressureChart(true)}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1">
                  <p className="text-gray-400 text-center mb-4 text-lg">
                    No data available
                  </p>
                </div>
              )}
            </div>

            <div
              style={{
                position: "fixed",
                top: 0,
                right: showSummarySlideOver ? 0 : "-450px",
                width: "450px",
                maxWidth: "55vw",
                height: "100vh",
                background: "#1C1C1E",
                borderLeft: "1px solid #363636",
                transition: "right 0.3s ease-in-out",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                boxShadow: showSummarySlideOver
                  ? "-4px 0 20px rgba(0, 0, 0, 0.5)"
                  : "none",
              }}
            >
              <div
                style={{
                  padding: "24px",
                  borderBottom: "1px solid #363636",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h4
                  style={{
                    fontFamily: "Figtree, sans-serif",
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                    margin: 0,
                  }}
                >
                  Summary of Preferred Cycles
                </h4>
                <button
                  onClick={() => setShowSummarySlideOver(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "6px",
                    borderRadius: "6px",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="#B0B4B7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  overflow: "auto",
                  padding: "24px",
                }}
              >
                {data && renderPressureTable(true)}
              </div>
            </div>

            {showSummarySlideOver && (
              <div
                onClick={() => setShowSummarySlideOver(false)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0, 0, 0, 0.4)",
                  zIndex: 999,
                }}
              />
            )}
          </div>
        </div>
      </IonModal>
      )}

      <style>{`
        .landscape-modal {
          --width: 100%;
          --height: 100%;
          --max-width: none;
          --max-height: none;
        }
        
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: rgba(107, 114, 128, 0.8) rgba(55, 65, 81, 0.3);
        }
        
        .scrollbar-thin::-webkit-scrollbar {
          width: 8px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(107, 114, 128, 0.8);
          border-radius: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 1);
        }
      `}</style>
    </>
  );
};

// Enhanced mock data with cycle feedback
export const mockEnhancedTitrationData: TitrationData = {
  therapyDate: "24 March 2025 ( 5:45:05 PM )",
  cycles: [
    {
      id: 1,
      pause: 8,
      inspiration: 45,
      expiration: -35,
      feedback: "preferred",
    },
    {
      id: 2,
      pause: 12,
      inspiration: 60,
      expiration: -45,
      feedback: "preferred",
    },
    {
      id: 3,
      pause: 6,
      inspiration: 35,
      expiration: -25,
      feedback: "preferred",
    },
    {
      id: 4,
      pause: 10,
      inspiration: 50,
      expiration: -40,
      feedback: "preferred",
    },
    { id: 5, pause: 5, inspiration: 30, expiration: -20, feedback: "none" },
    { id: 6, pause: 7, inspiration: 40, expiration: -30, feedback: "none" },
    { id: 7, pause: 9, inspiration: 55, expiration: -50, feedback: "none" },
    { id: 8, pause: 4, inspiration: 25, expiration: -15, feedback: "none" },
    {
      id: 9,
      pause: 15,
      inspiration: 70,
      expiration: -70,
      feedback: "disliked",
    },
    {
      id: 10,
      pause: 2,
      inspiration: 15,
      expiration: -10,
      feedback: "disliked",
    },
    {
      id: 11,
      pause: 13,
      inspiration: 65,
      expiration: -65,
      feedback: "disliked",
    },
    {
      id: 12,
      pause: 15,
      inspiration: 10,
      expiration: -12,
      feedback: "disliked",
    },
  ],
  pressureSettings: [
    { label: "Min pressure cmH₂O", pause: 5, insp: 20, exp: -35 },
    { label: "Max pressure cmH₂O", pause: 5, insp: 25, exp: -45 },
    { label: "Avg pressure cmH₂O", pause: 2.5, insp: 22.2, exp: 2.5 },
    { label: "Avg duration (Sec)", pause: 2, insp: 2.2, exp: 2.5 },
  ],
};

const TitrationDemo = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSync = (session: TitrationSession) => {
    console.log("Syncing therapy with refId:", session.refId);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <EnhancedTitrationCard
        data={mockEnhancedTitrationData}
        isLoading={isLoading}
        onSync={handleSync}
      />
    </div>
  );
};

export default TitrationDemo;
